import { useState } from 'react';
import type { FormEvent } from 'react';
import {
  esquemaCorreo,
  esquemaRestablecerPorCodigo,
  LARGO_MINIMO_CONTRASENA,
} from '@copathy/shared';
import { api, ErrorApi } from '../lib/api';
import { Boton } from './Boton';
import { Campo } from './Campo';
import { Candado, Correo, Flecha, Ojo, OjoTachado } from './iconos';

/**
 * Segundo panel del riel de la pantalla de acceso: recuperar la contraseña.
 *
 * ── Esto era un maniquí, y hay que decirlo porque explica el diseño ──
 * Hasta el 2026-09-07 este formulario **no llamaba a ningún endpoint**: esperaba
 * 600 ms con un `setTimeout`, ponía «Revisa tu correo» y mostraba un aviso
 * amarillo diciendo que el correo saliente todavía no existía. Ese aviso quedó
 * de antes de M10 y dejó de ser cierto: el correo sale de verdad desde D-092, y
 * el backend tiene el flujo completo desde D-078.
 *
 * Lo peor no era que no funcionara, era que **la pantalla afirmaba haber enviado
 * algo**. Alguien esperando un correo que nunca se pidió no tiene forma de saber
 * que el problema no es su bandeja.
 *
 * ── Un CÓDIGO, no un enlace ──
 * El texto anterior prometía «un enlace para crear una contraseña nueva». El
 * flujo que existe usa un **código de 6 dígitos** (D-078), el mismo que el
 * registro. Se usa ese y no se construye un segundo camino con enlace: dos
 * implementaciones del mismo cambio de estado divergen, y la que nadie mira se
 * queda sin la comprobación que la otra sí tiene.
 *
 * Es además **el mismo flujo que la app** (`movil/src/pantallas/Olvide.tsx`), así
 * que hay un solo par de endpoints que mantener.
 *
 * ── Por qué nunca se dice si el correo existe ──
 * `/codigo-correo` responde 204 exista o no la cuenta, y esta pantalla dice
 * «si tiene una cuenta». Responder distinto convertiría el login en un
 * verificador de quién está registrado: se prueba una lista de correos y los que
 * contestan diferente son los que existen. Por eso el paso 2 aparece igual en
 * los dos casos, y el código simplemente no llega si la cuenta no está.
 */
export function FormularioRecuperar({ alVolver }: { alVolver: () => void }) {
  const [paso, setPaso] = useState<'correo' | 'codigo' | 'listo'>('correo');
  const [correo, setCorreo] = useState('');
  const [codigo, setCodigo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [verClave, setVerClave] = useState(false);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function pedirCodigo(e: FormEvent) {
    e.preventDefault();
    const r = esquemaCorreo.safeParse(correo);
    if (!r.success) {
      setErrores({ correo: r.error.issues[0]?.message ?? 'Revisa el correo.' });
      return;
    }
    setErrores({});
    setErrorGeneral(null);
    setEnviando(true);
    try {
      await api.post('/api/auth/codigo-correo', { correo: r.data });
      setCorreo(r.data);
      setPaso('codigo');
    } catch (err) {
      /*
        Un fallo acá es de red o del servidor, nunca «ese correo no existe»: el
        endpoint responde 204 en los dos casos. Decir otra cosa sería inventar.
      */
      setErrorGeneral(
        err instanceof ErrorApi
          ? err.message
          : 'No pudimos conectar con el servidor. Revisa tu conexión.',
      );
    } finally {
      setEnviando(false);
    }
  }

  async function restablecer(e: FormEvent) {
    e.preventDefault();
    // El MISMO esquema Zod que valida el servidor.
    const r = esquemaRestablecerPorCodigo.safeParse({ correo, codigo, contrasena });
    if (!r.success) {
      const campos: Record<string, string> = {};
      for (const issue of r.error.issues) {
        const clave = String(issue.path[0] ?? '_');
        campos[clave] ??= issue.message;
      }
      setErrores(campos);
      return;
    }
    setErrores({});
    setErrorGeneral(null);
    setEnviando(true);
    try {
      await api.post('/api/auth/restablecer', r.data);
      setPaso('listo');
    } catch (err) {
      if (err instanceof ErrorApi) {
        if (err.campos) setErrores(err.campos);
        else setErrorGeneral(err.message);
      } else {
        setErrorGeneral('No pudimos conectar con el servidor. Revisa tu conexión.');
      }
    } finally {
      setEnviando(false);
    }
  }

  const volver = (
    <button
      type="button"
      onClick={alVolver}
      className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-marino-600 hover:text-naranja-600"
    >
      <Flecha size={16} /> Volver a ingresar
    </button>
  );

  if (paso === 'listo') {
    return (
      <div>
        <h2 className="text-2xl font-bold text-marino-800">Tu contraseña quedó lista</h2>
        <p className="mt-2 text-sm leading-relaxed text-marino-400">
          Ya puedes ingresar con la contraseña nueva.
        </p>
        {/*
          ⚠️ Se avisa que las demás sesiones se cerraron, y no es un detalle: el
          servidor revoca TODAS al restablecer (D-078). Quien hace esto porque
          sospecha que alguien entró a su cuenta necesita saber que el otro
          quedó afuera; y quien lo hace por olvido, entender por qué su teléfono
          le vuelve a pedir la contraseña.
        */}
        <p className="mt-4 rounded-lg border border-marino-100 bg-marino-50 px-4 py-3 text-xs text-marino-600">
          Por seguridad se cerraron las sesiones abiertas en otros dispositivos.
        </p>
        <Boton onClick={alVolver} className="mt-6 w-full">
          Ingresar
        </Boton>
      </div>
    );
  }

  if (paso === 'codigo') {
    return (
      <div>
        <h2 className="text-2xl font-bold text-marino-800">Revisa tu correo</h2>
        <p className="mt-2 text-sm leading-relaxed text-marino-400">
          Si <strong className="text-marino-700">{correo}</strong> tiene una cuenta, le llega
          un código de 6 dígitos. Escríbelo acá con tu contraseña nueva.
        </p>

        <form onSubmit={restablecer} noValidate className="mt-6 space-y-4">
          <Campo
            etiqueta="Código de 6 dígitos"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
            error={errores.codigo}
          />

          <Campo
            etiqueta="Contraseña nueva"
            type={verClave ? 'text' : 'password'}
            autoComplete="new-password"
            icono={<Candado size={18} />}
            value={contrasena}
            onChange={(e) => setContrasena(e.target.value)}
            error={errores.contrasena}
            derecha={
              <button
                type="button"
                onClick={() => setVerClave((v) => !v)}
                title={verClave ? 'Ocultar' : 'Mostrar'}
                aria-label={verClave ? 'Ocultar contraseña' : 'Ver contraseña'}
                className="rounded-full p-2.5 text-marino-400 transition hover:bg-marino-50 hover:text-marino-700"
              >
                {verClave ? <OjoTachado size={18} /> : <Ojo size={18} />}
              </button>
            }
          />
          {!errores.contrasena && (
            <p className="-mt-2 pl-5 text-xs text-marino-400">
              Mínimo {LARGO_MINIMO_CONTRASENA} caracteres.
            </p>
          )}

          {errorGeneral && (
            <p role="alert" className="text-sm font-medium text-red-600">
              {errorGeneral}
            </p>
          )}

          <Boton type="submit" cargando={enviando} className="w-full">
            {enviando ? 'Guardando…' : 'Cambiar mi contraseña'}
          </Boton>
        </form>

        <button
          type="button"
          onClick={() => {
            setPaso('correo');
            setCodigo('');
            setErrores({});
            setErrorGeneral(null);
          }}
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-marino-600 hover:text-naranja-600"
        >
          <Flecha size={16} /> No me llegó el código
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-marino-800">Recuperar contraseña</h2>
      <p className="mt-1.5 text-sm text-marino-400">
        Escribe tu correo y te enviamos un código para crear una nueva.
      </p>

      <form onSubmit={pedirCodigo} noValidate className="mt-6 space-y-5">
        <Campo
          etiqueta="Correo electrónico"
          type="email"
          autoComplete="email"
          inputMode="email"
          icono={<Correo size={18} />}
          value={correo}
          onChange={(e) => setCorreo(e.target.value)}
          error={errores.correo}
        />

        {errorGeneral && (
          <p role="alert" className="text-sm font-medium text-red-600">
            {errorGeneral}
          </p>
        )}

        <Boton type="submit" cargando={enviando} className="w-full">
          {enviando ? 'Enviando…' : 'Enviar código'}
        </Boton>
      </form>

      {volver}
    </div>
  );
}
